/**
 * MyProjectView — read-only detail of the engineer's projects: CURRENT (on
 * their calendar from today on) and PREVIOUS (10/9/26), each with the days
 * they were on it.
 *
 * Fetches GET /api/engineer/my-project (scoped server-side to the engineer's
 * own calendar; projects found by the calendar's GLOBAL #). Renders descriptive, NON-financial detail
 * only — description, customer, GLOBAL number, approval manager, PO number,
 * status. There is no price/cost/margin here by design, and no phase or
 * completion % (10/8/26: every row was Proposal / 0, so they said nothing).
 *
 * Not-linked accounts get the same friendly guidance card as My Schedule.
 */
import { useEffect, useState } from 'react';

const CARD = {
  background: '#fff',
  border: '1px solid #ECECEC',
  borderRadius: 12,
  boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
  padding: '18px 20px',
  marginBottom: 14,
};

const statusColor = (s) => {
  const v = (s || '').toLowerCase();
  if (v === 'won') return '#1B5E20';
  if (v === 'lost') return '#B71C1C';
  if (v === 'open') return '#1565C0';
  return '#555';
};

function Field({ label, value, mono }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 9.5, fontWeight: 800, color: '#999', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 3 }}>
        {label}
      </div>
      <div
        style={{
          fontSize: 12.5,
          fontWeight: 600,
          color: '#0D0D0D',
          fontVariantNumeric: mono ? 'tabular-nums' : 'normal',
          wordBreak: 'break-word',
        }}
      >
        {value || '-'}
      </div>
    </div>
  );
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const day = (iso) => {
  const [y, m, d] = String(iso).split('-').map(Number);
  return `${MONTHS[m - 1]} ${d}, ${y}`;
};
/** "On the calendar Oct 19, 2026 - Feb 13, 2027 · 88 days" */
const whenLine = (p) => {
  if (!p.firstDay) return 'Your assigned project';
  const span = p.firstDay === p.lastDay ? day(p.firstDay) : `${day(p.firstDay)} - ${day(p.lastDay)}`;
  return `On your calendar ${span} · ${p.days} day${p.days === 1 ? '' : 's'}`;
};

function ProjectCard({ p }) {
  return (
    <div style={CARD}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 14 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: '#0D0D0D', marginBottom: 3 }}>
            {p.name || p.calendarLabel || 'Untitled project'}
          </div>
          <div style={{ fontSize: 12, color: '#666' }}>
            {p.customer || '-'}
            {p.subCustomer ? ` · ${p.subCustomer}` : ''}
          </div>
          <div style={{ fontSize: 11, color: '#1B5E20', fontWeight: 700, marginTop: 4 }}>
            {whenLine(p)}
            {p.calendarLabel && p.name ? <span style={{ color: '#888', fontWeight: 500 }}> · calendar: {p.calendarLabel}</span> : null}
          </div>
          {p.found === false && (
            <div style={{ fontSize: 11, color: '#8D6E00', marginTop: 4 }}>
              No GLOBAL # on the calendar for this yet - ask the office to add the Project #.
            </div>
          )}
        </div>
        {p.status && (
          <span
            style={{
              fontSize: 10.5,
              fontWeight: 800,
              color: '#fff',
              background: statusColor(p.status),
              borderRadius: 20,
              padding: '3px 11px',
              whiteSpace: 'nowrap',
            }}
          >
            {p.status}
          </span>
        )}
      </div>

      {p.description && <p style={{ fontSize: 12.5, color: '#333', lineHeight: 1.6, margin: '0 0 16px' }}>{p.description}</p>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 14 }}>
        <Field label="GLOBAL Project #" value={p.globalProjectNumber} mono />
        <Field label="PO Number" value={p.poNumber} mono />
        <Field label="Approval Manager" value={p.approvalManager} />
        <Field label="Project Manager" value={p.pm} />
        <Field label="Division" value={p.division} />
        <Field label="Type" value={p.type} />
      </div>
      {p.alsoUnderThisNumber && p.alsoUnderThisNumber.length > 0 && (
        <div style={{ fontSize: 11, color: '#777', marginTop: 12 }}>Also under this number: {p.alsoUnderThisNumber.join(' · ')}</div>
      )}
    </div>
  );
}

export default function MyProjectView() {
  const [state, setState] = useState({ loading: true, error: null, data: null });

  useEffect(() => {
    let alive = true;
    fetch('/api/engineer/my-project')
      .then((r) => r.json())
      .then((data) => {
        if (alive) setState({ loading: false, error: null, data });
      })
      .catch((err) => {
        if (alive) setState({ loading: false, error: err.message || 'Failed to load', data: null });
      });
    return () => {
      alive = false;
    };
  }, []);

  const { loading, error, data } = state;

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#888', fontFamily: "'Montserrat', sans-serif", fontSize: 13 }}>
        Loading your project…
      </div>
    );
  }
  if (error) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#B71C1C', fontFamily: "'Montserrat', sans-serif", fontSize: 13 }}>
        Couldn't load your project. Please try again.
      </div>
    );
  }

  if (data && data.linked === false) {
    return (
      <div style={{ fontFamily: "'Montserrat', sans-serif", display: 'flex', justifyContent: 'center', paddingTop: 40 }}>
        <div
          style={{
            maxWidth: 460,
            textAlign: 'center',
            background: '#FFF8E1',
            border: '1px solid #FFE082',
            borderRadius: 12,
            padding: '28px 26px',
          }}
        >
          <div style={{ fontSize: 11, fontWeight: 800, color: '#8D6E00', letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 8 }}>
            Account not linked
          </div>
          <div style={{ fontSize: 17, fontWeight: 800, color: '#5D4A00', marginBottom: 10 }}>No project to show yet</div>
          <p style={{ fontSize: 12.5, color: '#6D5A1A', lineHeight: 1.6, margin: 0 }}>
            {data.message || 'Your account is not linked to an engineer record yet. Ask an admin to link you in Team & Access.'}
          </p>
        </div>
      </div>
    );
  }

  const current = data?.current || data?.projects || [];
  const previous = data?.previous || [];
  const engName = data?.engineer?.name;
  const heading = (t) => <div style={{ margin: '6px 0 10px', fontSize: 13, fontWeight: 800, color: '#0D0D0D' }}>{t}</div>;

  return (
    <div style={{ fontFamily: "'Montserrat', sans-serif" }}>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: '#1B5E20', letterSpacing: 2, textTransform: 'uppercase' }}>
          Engineer · My Project
        </div>
        <p style={{ fontSize: 12.5, color: '#555', margin: '4px 0 0' }}>
          {engName ? (
            <>
              Projects for <b>{engName}</b>, from the manpower calendar.
            </>
          ) : (
            'Your projects, from the manpower calendar.'
          )}
        </p>
      </div>

      {heading('Current & upcoming')}
      {current.length > 0 ? (
        current.map((p, i) => <ProjectCard key={`${p.globalProjectNumber || p.calendarLabel || p.name}-${i}`} p={p} />)
      ) : (
        <div style={{ ...CARD, textAlign: 'center', color: '#999', fontSize: 12.5, padding: 28 }}>
          No current project assigned. Once you're scheduled on a project it'll appear here.
        </div>
      )}
      {previous.length > 0 && (
        <>
          {heading('Previous')}
          {previous.map((p, i) => (
            <ProjectCard key={`prev-${p.globalProjectNumber || p.calendarLabel}-${i}`} p={p} />
          ))}
        </>
      )}
    </div>
  );
}
